import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Seo from '../components/Seo'
import { getCmsCollection, getCmsPage } from '../lib/cms'
import { getOptimizedImageUrl } from '../lib/image'

export default function ServicesPage() {
  const [services, setServices] = useState([])
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      try {
        const [data, page] = await Promise.all([
          getCmsCollection('tjenester'),
          getCmsPage('tjenester'),
        ])
        if (page) setPageData(page)
        const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0))
        setServices(sorted)
      } catch (err) {
        console.error('Failed to load services:', err)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const headerTitle = pageData?.hero_title || 'Tjenester'
  const headerDesc = pageData?.hero_description || 'Værdi er en subjektiv opfattelse af noget, der giver en følelse af betydning, nytte eller mening.'

  return (
    <>
      <Seo
        title="Tjenester :: Lyn Entreprenør"
        description="Værdi er en subjektiv opfattelse af noget, der giver en følelse af betydning, nytte eller mening."
        ogImage="https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/design-8_25d48453.png"
        canonicalUrl="https://www.lyn-ent.dk/tjenester/"
      />

      {/* Page Header (Light & Architectural) */}
      <section className="bg-red-600 py-16 sm:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-left">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            {headerTitle}
          </h1>

          <p className="mt-4 text-lg sm:text-xl text-red-100 max-w-3xl leading-relaxed">
            {headerDesc}
          </p>
        </div>

        {/* V-shaped divider at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg
            className="relative block w-full h-8 sm:h-12 md:h-16 lg:h-20 text-white"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
          </svg>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-pulse">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-72 rounded-2xl bg-slate-100" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const serviceSlug =
                  typeof service.slug === 'object' ? service.slug?.da : service.slug
                // Map the system slug "tjeneste-jordboring" to route "/tjenester/jordboring"
                const cleanSlug = serviceSlug ? serviceSlug.replace(/^tjeneste-|^service-/, '') : ''
                const image = service.featured_image?.[0]

                return (
                  <Link
                    key={service.id || index}
                    to={`/tjenester/${cleanSlug}`}
                    className="flex flex-col justify-between rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden group"
                  >
                    {/* Card Image */}
                    {image && (
                      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                        <img
                          src={getOptimizedImageUrl(image, { width: 600, height: 380, quality: 80 })}
                          alt={service.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                        {/* V-shaped divider at bottom of image */}
                        <div className="absolute -bottom-0.5 -left-px -right-px w-[calc(100%+2px)] overflow-hidden leading-none z-10 pointer-events-none">
                          <svg
                            className="relative block w-full h-5 sm:h-6 md:h-7 text-white"
                            viewBox="0 0 1200 120"
                            preserveAspectRatio="none"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Card Content */}
                    <div className="p-7 flex-1 flex flex-col justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-3">
                          {service.title}
                        </h2>
                        <p className="text-sm text-slate-600 leading-relaxed line-clamp-4">
                          {service.short_description || service.title}
                        </p>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </section>
    </>
  )
}
